/**
 * Helpers for the pre-resized listing photo variants written by
 * POST /api/listings/upload-photo: `<base>_xs|sm|md|lg|xl.webp` (320/640/1024/1600/1920 px).
 *
 * The API hands out the `_md` (1024 px) URL as the canonical photo URL. Anything
 * that renders a thumbnail/card must step down to a smaller variant instead of
 * downloading the 1024 px file — and must fall back to the URL unchanged for
 * legacy photos that have no variants (avatars, posts, chat, community images).
 */

const VARIANT_RE = /_(xs|sm|md|lg|xl)\.webp(\?.*)?$/i;

export const PHOTO_VARIANTS = [
  { name: "xs", width: 320 },
  { name: "sm", width: 640 },
  { name: "md", width: 1024 },
  { name: "lg", width: 1600 },
  { name: "xl", width: 1920 },
];

export function hasPhotoVariants(url) {
  return typeof url === "string" && VARIANT_RE.test(url);
}

/** Swap to a specific variant; returns the URL unchanged when it has no variants. */
export function photoVariant(url, name) {
  if (!hasPhotoVariants(url)) return url;
  return url.replace(VARIANT_RE, (_m, _v, q) => `_${name}.webp${q || ""}`);
}

/**
 * srcset for the given variant names (default xs/sm/md — enough for any card or
 * thumbnail). Returns undefined when the URL has no variants.
 */
export function photoSrcSet(url, names = ["xs", "sm", "md"]) {
  if (!hasPhotoVariants(url)) return undefined;
  return PHOTO_VARIANTS.filter((v) => names.includes(v.name))
    .map((v) => `${photoVariant(url, v.name)} ${v.width}w`)
    .join(", ");
}

/** Props for a card/thumbnail <img>: small default src + responsive srcset + lazy decode. */
export function thumbImgProps(url, sizes = "(max-width: 600px) 50vw, 240px") {
  const srcSet = photoSrcSet(url);
  return {
    src: photoVariant(url, "sm"),
    ...(srcSet ? { srcSet, sizes } : {}),
    loading: "lazy",
    decoding: "async",
    ...(hasPhotoVariants(url) ? { onError: fallbackToOriginal(url) } : {}),
  };
}

/**
 * `onError` handler that makes a missing variant file harmless (every listing
 * photo currently has all variants, but a few early/interrupted uploads never got
 * the larger ones — and a browser does NOT try another srcset candidate on error).
 *
 * First failure: drop srcset/sizes and load `fallbackUrl` (the URL as stored, which
 * the API always serves). If that fails too and `finalFallback` is given, show it.
 * State lives on the element (keyed by the URL), so a recycled <img> for a
 * different photo starts fresh and an error loop is impossible.
 */
export function fallbackToOriginal(fallbackUrl, finalFallback) {
  return (e) => {
    const img = e && e.currentTarget;
    if (!img) return;
    if (fallbackUrl && img.dataset.variantFallback !== fallbackUrl) {
      img.dataset.variantFallback = fallbackUrl;
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      if (img.getAttribute("src") !== fallbackUrl) {
        img.src = fallbackUrl;
        return;
      }
    }
    if (finalFallback && img.getAttribute("src") !== finalFallback) {
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.src = finalFallback;
    }
  };
}
